import { useAuth, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useTrackingEvent, useTrpcMutation } from '@vrplatform/data';
import { showErrorNotification } from '@vrplatform/theme';
import {
  showSuccessNotification,
  showWarnNotification,
} from '@vrplatform/theme';
import { toTitleCase } from '@vrplatform/utils';
import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  getClerkErrorMessage,
  isAlreadySignedInClerkError,
} from './useSignInForm.utils';

export type ResetPasswordFormInputs = {
  code: string;
  password: string;
  confirmPassword: string;
};

export type SignInFormInputs = {
  email: string;
  password: string;
};

export type SignUpFormInputs = {
  firstName: string;
  lastName: string;
  companyName: string;
  type: 'propertyManager' | 'partner';
  email: string;
  password: string;
  policyAccepted: boolean;
};

export function useSignInForm() {
  const track = useTrackingEvent();

  const { isSignedIn } = useAuth();
  const { signIn, setActive: setSignInActive } = useSignIn();
  const { signUp, setActive, isLoaded: isSignupLoaded } = useSignUp();
  const [showSignUpVerification, setShowSignUpVerification] = useState(false);
  const savedSignUp = useRef<SignUpFormInputs | null>(null);
  const [resetPasswordEmail, setResetPasswordEmail] = useState<string | null>(
    null
  );
  const [showSecondFactor, setShowSecondFactor] = useState(false);

  const location = useLocation();
  const goto = useNavigate();

  const [loading, setLoading] = useState(false);

  const { mutate: checkExistingOwners } = useTrpcMutation(
    'checkExistingOwnerUser'
  );
  const postLoginHref = `/${location.search}`;

  // Handle sign up / new user flow
  const submitSignUp = async (formData: SignUpFormInputs | undefined) => {
    setLoading(true);

    try {
      // When user resends email we cannot use form state anymore, but instead use saved ref
      const data = formData || savedSignUp.current;

      if (!data?.email) throw new Error('Missing email address.');

      const { ok } = await checkExistingOwners({ email: data.email });

      if (!ok) {
        showWarnNotification({
          title: 'Account already exists',
          message: `It looks like you already have an account. If you haven't received an inivitation yet, please reach out to your property manager or partner.`,
        });
        setLoading(false);
        return;
      }

      const result = await signUp?.create({
        firstName: data.firstName,
        lastName: data.lastName,
        emailAddress: data.email,
        password: data.password,
        unsafeMetadata: {
          onboardingV2: true,
          companyName: data.companyName,
          tenantType: data.type,
        },
      });

      if (result?.status === 'complete') {
        setActive!({ session: result.createdSessionId });
      } else {
        const res = await signUp?.prepareEmailAddressVerification({
          strategy: 'email_code',
        });

        if (res?.id) {
          // When user send sign up for first time, we save form state
          savedSignUp.current = data;
          setShowSignUpVerification(true);
          goto('/verify-email');
          track('account_signup_code_requested', {
            firstName: data.firstName,
            lastName: data.lastName,
            userEmail: data.email,
            companyName: data.companyName,
            type:
              data.type === 'propertyManager' ? 'property_manager' : 'partner',
            acceptedTerms: data.policyAccepted,
          } as any);
          showSuccessNotification({
            title: 'Success!',
            message: 'Please check your email to confirm your account.',
          });
        }
      }
    } catch (err: any) {
      console.error(err?.message || err);
      const errorMessage = getClerkErrorMessage(
        err,
        "We couldn't sign you up."
      );
      showErrorNotification({
        title: "We couldn't sign you up.",
        message: errorMessage,
      });
    }

    setLoading(false);
  };

  // Handle email with password flow
  const submitLogin = async (data: SignInFormInputs) => {
    if (!data.password) {
      showErrorNotification({ message: 'Please enter a password.' });
      return;
    }

    if (isSignedIn) {
      goto(postLoginHref);
      return;
    }

    setLoading(true);

    if (!signIn) return console.error('no sign in');

    await signIn
      .create({
        identifier: data.email,
        password: data.password,
      })
      .then(async (result) => {
        if (result.status === 'complete') {
          await setSignInActive({
            session: result.createdSessionId,
            // beforeEmit: (session) => {
            //   const externalUserId = session?.user.externalId;

            //   track('account_signin_completed', {
            //     firstName: result.userData.firstName,
            //     lastName: result.userData.lastName,
            //     userId: externalUserId || undefined,
            //     userEmail: result.identifier || undefined,
            //     usedSecondFactor: false,
            //   });
            // },
          });
          goto(postLoginHref);
        } else if (result.status === 'needs_second_factor') {
          setShowSecondFactor(true);
        } else {
          // Failed to sign in
          console.error(result);
          throw new Error(`Failed to sign in: ${result.status}`);
        }
      })
      .catch((err) => {
        if (isAlreadySignedInClerkError(err)) {
          goto(postLoginHref);
          return;
        }

        const errorMessage = getClerkErrorMessage(err, 'Please try again.');
        console.error('error', errorMessage);
        showErrorNotification({
          title: "We couldn't sign you in.",
          message: errorMessage,
        });
      });

    setLoading(false);
  };
  const showingSignUp =
    location.pathname === '/sign-up' || location.pathname === '/choose-role';

  const redirectTo = (view: 'login' | 'sign-up') => goto(`/${view}`);

  const returnToDefault = () => {
    savedSignUp.current = null;
    setLoading(false);
    setResetPasswordEmail(null);
    setShowSignUpVerification(false);
    redirectTo('login');
    setShowSecondFactor(false);
  };

  const triggerPasswordReset = async (
    email: string
  ): Promise<{ ok: boolean }> => {
    if (!signIn) {
      showErrorNotification({
        title: 'An error occurred',
        message:
          'Missing Sign In Function. Please refresh your browser window.',
      });

      return {
        ok: false,
      };
    }

    return await signIn!
      .create({
        identifier: email,
        strategy: 'reset_password_email_code',
      })
      .then((_res) => {
        // track('account_password_reset_requested', { userEmail: email }); => legacy event!

        showSuccessNotification({
          title: 'Email sent!',
          message:
            'You will shortly receive a code in your email with which you can reset your password.',
        });

        // set reset password email to redirect to next view
        setResetPasswordEmail(email);
        goto('/reset-password');

        return {
          ok: true,
        };
      })
      .catch((error) => {
        console.error(error);

        showErrorNotification({
          message:
            error?.errors?.[0]?.longMessage ||
            error?.errors?.[0]?.message ||
            'Please try again.',
          title: 'Failed to send password reset email',
        });

        return {
          ok: false,
        };
      });
  };

  const handlePasswordReset = async (data: ResetPasswordFormInputs) => {
    if (!signIn) {
      showErrorNotification({
        title: 'An error occurred',
        message:
          'Missing Sign In Function. Please refresh your browser window.',
      });
    }

    await signIn!
      .attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: data.code,
        password: data.password,
      })
      .then((result) => {
        if (result.status === 'needs_second_factor') {
          setShowSecondFactor(true);
        } else if (result.status === 'complete') {
          setActive?.({ session: result.createdSessionId });
          track('account_password_reset_completed', {
            userEmail: resetPasswordEmail || undefined,
          });

          goto('/');
          showSuccessNotification({
            title: 'Password reset',
            message: 'Your password was successfully reset.',
          });
        }
        //  else
        //   track('account_password_reset_failed', {
        //     userEmail: resetPasswordEmail || undefined,
        //     status: result.status,
        //   }); => legacy event!
      })
      .catch((error) => {
        console.error(JSON.stringify(error, null, 2));

        let message: string =
          error?.errors?.[0]?.longMessage ||
          error?.errors?.[0]?.message ||
          'Please try again.';
        let title: string =
          toTitleCase(error?.errors?.[0]?.code) || 'Failed to reset password.';

        if (message === 'Update operations are not allowed on older sign ins') {
          message =
            'Please close any duplicate tabs, refresh the page and try again.';
        }

        if (title === 'Resource Forbidden') {
          title = 'Sign-in flow was updated';
        }

        if (title === 'Client State Invalid') {
          title = 'Sign-up state has updated or expired';
          message = 'Please refresh the page and try again.';
        }

        // track('account_password_reset_failed', {
        //   userEmail: resetPasswordEmail || undefined,
        //   status: error?.errors?.[0]?.code,
        // }); => legacy event!

        showErrorNotification({
          message,
          title,
        });
      });
  };

  // This verifies the user using email code that is delivered.
  const handleCodeConfirmation = async (code: string) => {
    if (!isSignupLoaded) {
      return;
    }

    const data = savedSignUp.current;

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code,
      });
      if (completeSignUp.status === 'complete') {
        await setActive({ session: completeSignUp.createdSessionId });
        track('account_signup_code_completed', {
          firstName: data?.firstName,
          lastName: data?.lastName,
          userEmail: data?.email || undefined,
          companyName: data?.companyName,
          type:
            data?.type === 'propertyManager' ? 'property_manager' : 'partner',
        });
        goto('/');
      } else {
        /*  investigate the response, to see if there was an error
           or if the user needs to complete more steps.*/
        // track('account_signup_code_failed', {
        //   firstName: data?.firstName,
        //   lastName: data?.lastName,
        //   userEmail: data?.email || undefined,
        //   companyName: data?.companyName,
        //   type:
        //     data?.type === 'propertyManager' ? 'property_manager' : 'partner',
        //   status: completeSignUp.status,
        // }); => legacy event!
        console.error(JSON.stringify(completeSignUp, null, 2));
        throw new Error('Failed to complete sign up.');
      }
    } catch (error: any) {
      // track('account_signup_code_failed', {
      //   firstName: data?.firstName,
      //   lastName: data?.lastName,
      //   userEmail: data?.email || undefined,
      //   companyName: data?.companyName,
      //   type: data?.type === 'propertyManager' ? 'property_manager' : 'partner',
      //   status: error?.errors?.[0]?.code,
      // }); => legacy event!

      showErrorNotification({
        message:
          error?.errors?.[0]?.longMessage ||
          error?.errors?.[0]?.message ||
          'Please try again.',
        title:
          toTitleCase(error?.errors?.[0]?.code) || 'Failed to reset password.',
      });
    }
  };

  const submitSecondFactor = async ({
    code,
    strategy,
  }: {
    code: string;
    strategy: 'totp' | 'backup_code';
  }) => {
    try {
      await signIn
        ?.attemptSecondFactor({
          code,
          strategy,
        })
        .then((result) => {
          if (result.status === 'complete') {
            setSignInActive({
              session: result.createdSessionId,
              // beforeEmit: (session) => {
              //   const externalUserId = session?.user.externalId;

              //   track('account_signin_completed', {
              //     firstName: result.userData.firstName,
              //     lastName: result.userData.lastName,
              //     userId: externalUserId || undefined,
              //     userEmail: result.identifier || undefined,
              //     usedSecondFactor: true,
              //   });
              // },
            });
            goto(postLoginHref);
          } else {
            console.error(result);
            throw new Error(`Failed to sign in: ${result.status}`);
          }
        });
    } catch (error: any) {
      console.error(JSON.stringify(error, null, 2));

      showErrorNotification({
        message:
          error?.errors?.[0]?.longMessage ||
          error?.errors?.[0]?.message ||
          'Please try again.',
        title: 'Failed to submit authentication',
      });
    }
  };

  return {
    returnToDefault,
    loading,
    showingSignUp,
    triggerPasswordReset,
    handlePasswordReset,
    handleResendVerification: submitSignUp,
    resetPasswordEmail,
    handleCodeConfirmation,
    submitLogin,
    submitSignUp,
    showSignUpVerification,
    showSecondFactor,
    submitSecondFactor,
  };
}
